import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Camera, FileText, Image as ImageIcon, Wallet, CheckSquare, Clock, MessageSquare } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Spinner, StatusBadge } from "@/components/common/Common";
import api from "@/lib/api";
import { inr } from "@/lib/constants";

export default function ClientPortal() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/client/${projectId}`).then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || "Access denied")); }, [projectId]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="client-error">{err}</div>;
  if (!data) return <div className="min-h-screen"><Spinner label="Loading your project..." /></div>;
  const p = data.project, b = data.budget || {};

  return (
    <div className="min-h-screen bg-sp-light" data-testid="client-portal">
      <header className="bg-sp-navy text-white"><div className="sp-container py-4 flex items-center justify-between"><Link to="/"><Logo dark /></Link><span className="text-xs text-white/60 uppercase tracking-wider">Client Portal</span></div></header>

      <div className="sp-container py-8">
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="relative h-48 bg-sp-navy">
            <img src="https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?q=85&w=1200" alt="" className="w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-t from-sp-navy to-transparent" />
            <div className="absolute bottom-4 left-6 text-white"><h1 className="text-2xl font-bold">{p.name}</h1><p className="text-white/70 text-sm">{p.location} · {p.floors} · {p.builtup_area} sq.ft</p></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100 border-b">
            <Metric label="Project Progress" value={`${p.progress}%`} />
            <Metric label="Budget Used" value={`${inr(b.spent)} / ${inr(b.planned)}`} />
            <Metric label="Status" value={p.status} />
            <Metric label="Health" value={`${p.health}/100`} />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            <Panel title="Construction Timeline" icon={Clock}>
              <div className="space-y-2.5">{data.stages.map((s) => (
                <div key={s.id} className="flex items-center gap-3"><span className="text-sm text-sp-navy w-28 shrink-0">{s.name}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-sp-orange" style={{ width: `${s.progress}%` }} /></div>
                  <span className="text-xs text-slate-400 w-9">{s.progress}%</span></div>
              ))}</div>
            </Panel>
            <Panel title="Site Reports" icon={FileText}>
              {data.site_reports.length === 0 ? <Empty /> : <div className="space-y-3">{data.site_reports.slice(0, 5).map((r) => (
                <div key={r.id} className="border-b last:border-0 pb-2"><div className="text-sm font-medium text-sp-navy">{r.date} · {r.weather}</div><div className="text-xs text-slate-500 mt-0.5">{r.work_completed}</div></div>
              ))}</div>}
            </Panel>
          </div>
          <div className="space-y-6">
            <Panel title="Live Site" icon={Camera}>
              {data.cameras.length === 0 ? <Empty text="No cameras configured" /> : data.cameras.map((c) => (
                <div key={c.id} className="rounded-lg bg-slate-900 aspect-video flex flex-col items-center justify-center text-white/60 mb-2"><Camera className="h-6 w-6" /><span className="text-xs mt-1">{c.name}</span><StatusBadge status={c.status} /></div>
              ))}
              <p className="text-[11px] text-slate-400 mt-1">Live RTSP/WebRTC integration ready — cameras appear here once connected.</p>
            </Panel>
            <Panel title="Approvals" icon={CheckSquare}>
              {data.approvals.length === 0 ? <Empty /> : <div className="space-y-2">{data.approvals.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-sm"><span className="text-sp-navy">{a.title}</span><StatusBadge status={a.status} /></div>
              ))}</div>}
            </Panel>
            <Panel title="Payments" icon={Wallet}>
              {data.payments.length === 0 ? <Empty /> : <div className="space-y-2">{data.payments.map((pm) => (
                <div key={pm.id} className="flex items-center justify-between text-sm"><span className="text-sp-navy">{pm.title}</span><span>{inr(pm.amount)} <StatusBadge status={pm.status} /></span></div>
              ))}</div>}
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
const Metric = ({ label, value }) => <div className="p-4 text-center"><div className="text-lg font-bold text-sp-navy">{value}</div><div className="text-[11px] text-slate-400 uppercase tracking-wider mt-0.5">{label}</div></div>;
const Panel = ({ title, icon: Icon, children }) => <div className="rounded-xl border border-slate-200 bg-white p-5"><div className="flex items-center gap-2 mb-4"><Icon className="h-4 w-4 text-sp-orange" /><span className="font-semibold text-sp-navy text-sm">{title}</span></div>{children}</div>;
const Empty = ({ text = "Nothing here yet" }) => <p className="text-sm text-slate-400 py-2">{text}</p>;
